import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import AliceCarousel from 'react-alice-carousel';
import 'react-alice-carousel/lib/alice-carousel.css';
import right from '../../assets/11. FICHA/right.svg';
import left from '../../assets/11. FICHA/left.svg';
import 'react-responsive-modal/styles.css';
import { Modal } from 'react-responsive-modal';
import Loader from '../../utils/carousel/Loader';
import { isMobile } from 'react-device-detect';
import translate from '../../components/languages';

const CheckoutCarousel = ({
  items,
  pureItems,
  setPagination = false,
  infinite = true,
  showArrows = false,
  showAllBtn = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const carouselFull = useRef(null);
  const abortController = useRef(null);

  useEffect(() => {
    if (loadedImages || !pureItems?.length) return;

    // Create abort controller for cleanup
    abortController.current = new AbortController();

    const loadImages = (images, attr) => {
      return images.map(
        (image) =>
          new Promise((resolve, reject) => {
            if (abortController.current?.signal.aborted) {
              reject(new Error('Aborted'));
              return;
            }

            const img = new Image();
            img.src = image[attr];
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('Failed to load image'));

            // Handle abort
            abortController.current.signal.addEventListener('abort', () => {
              img.onload = null;
              img.onerror = null;
              reject(new Error('Aborted'));
            });
          }),
      );
    };

    // Load images with proper error handling
    const loadAllImages = async () => {
      try {
        await Promise.all(loadImages(pureItems, 'mediumScaleImageUrl'));

        if (!isMobile && !abortController.current?.signal.aborted) {
          await Promise.all(loadImages(pureItems, 'originalImageUrl'));
        }

        if (!abortController.current?.signal.aborted) {
          setLoadedImages(true);
        }
      } catch (error) {
        if (error.message !== 'Aborted') {
          console.error('Error loading images:', error);
        }
      }
    };

    loadAllImages();

    // Cleanup function
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [pureItems, loadedImages]);

  // Cleanup modal effects on unmount
  useEffect(() => {
    return () => {
      // Reset body styles if component unmounts while modal is open
      if (modalIsOpen) {
        document.body.style.overflow = 'initial';
        document.body.style.height = 'initial';
      }
    };
  }, [modalIsOpen]);

  const responsive = {
    0: { items: 1 },
    678: { items: 3 },
    1280: { items: 3 },
  };

  const renderSlideInfo = useCallback(
    ({ item, itemsCount }) => {
      if (setPagination) setPagination(`${item}/${itemsCount}`);
    },
    [setPagination],
  );

  const closeModal = useCallback(() => {
    setModalIsOpen(false);
    document.body.style.overflow = 'initial';
    document.body.style.height = 'initial';
  }, []);

  const openModal = useCallback(() => {
    setModalIsOpen(true);
    document.body.style.overflow = 'hidden';
    document.body.style.height = '100%';
  }, []);

  const renderNextButton = useCallback(({ isDisabled }) => {
    return (
      <img
        src={right}
        alt="next button"
        className="alice-custom-right-btn"
        style={{ opacity: isDisabled ? '0.5' : 1 }}
      />
    );
  }, []);

  const renderPrevButton = useCallback(({ isDisabled }) => {
    return (
      <img
        src={left}
        className="alice-custom-left-btn"
        alt="previous button"
        style={{
          width: '16px',
          opacity: isDisabled ? '0.5' : 1,
          position: 'absolute',
          left: '5vw',
          bottom: '-12px',
        }}
      />
    );
  }, []);

  // Memoize image URLs to prevent recreation on each render
  const pureItemsImg = useMemo(() => {
    return (
      pureItems?.map((eachPhoto) => {
        return isMobile
          ? eachPhoto.mediumScaleImageUrl
          : eachPhoto.originalImageUrl;
      }) || []
    );
  }, [pureItems]);

  if (!loadedImages) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: '25vh' }}
      >
        <Loader />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {showAllBtn && items.length > 0 && (
        <button className="view-all-propics" onClick={openModal}>
          {translate.t('checkoutSeection.seeAllPhotos')}
        </button>
      )}

      <div className="container-top-carousel">
        <AliceCarousel
          mouseTracking
          items={items}
          infinite={infinite}
          renderPrevButton={showArrows ? renderPrevButton : undefined}
          renderNextButton={showArrows ? renderNextButton : undefined}
          responsive={responsive}
          animationType="fadeout"
          disableButtonsControls={!showArrows}
          renderSlideInfo={renderSlideInfo}
          renderDotsItem={false}
          disableDotsControls={true}
          disableSlideInfo={false}
        />
      </div>

      {modalIsOpen && (
        <FullScreenCarousel onClose={closeModal} images={pureItemsImg} />
      )}
    </div>
  );
};

export default CheckoutCarousel;

const FullScreenCarousel = ({ onClose, images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1,
    );
  }, [images.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, goToPrevious, goToNext]);

  return (
    <div className="full-div-carousel">
      <button onClick={onClose} className="carousel-button close-btn">
        &times;
      </button>
      <button onClick={goToPrevious} className="carousel-button prev">
        &#9664;
      </button>
      <div className="carousel-image-container">
        <img
          src={images[currentIndex]}
          alt={`Slide ${currentIndex + 1} of ${images.length}`}
          className="carousel-image"
          loading="lazy"
        />
      </div>
      <button onClick={goToNext} className="carousel-button next">
        &#9654;
      </button>
      <div className="carousel-indicators">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};
